// Item 409 — the pose skeleton over the event video, for superusers (the owner's mockup option B).
// PoseLayer gates (superuser + a stored pose), fetches, and holds the "Show pose" switch; PoseOverlay is the
// canvas. The canvas covers the whole media area and is drawn against the video's REAL painted box (the
// object-contain letterbox), read from the element on every frame. A resize or a playing clip redraws by itself;
// a zoom or pan is only a CSS transform (with a 0.15 s transition) and fires no video or resize event, so the
// caller passes `transformKey` and a change of it redraws on every animation frame for TRANSFORM_REDRAW_MS.
import { useEffect, useId, useRef, useState, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import * as Switch from '@radix-ui/react-switch'
import { eventsApi } from '@/api/endpoints'
import { POSE_STALE_MS, queryKeys } from '@/config/queryConfig'
import { PoseGate } from './PoseGate'
import { useAuthStore } from '@/store/authStore'
import { cn } from '@/lib/utils'
import { COCO_EDGES, WRISTS, frameAt, toScreen, videoContentRect } from '@/lib/poseGeometry'
import type { Event, EventPose } from '@/types'

const MIN_SCORE = 0.3
const TRAIL_S = 1
// the modal's zoom transition is 150 ms; redraw a little longer so the last frame is the final position
const TRANSFORM_REDRAW_MS = 300
const TEAL = '#14b8a6'
const AMBER = '#f59e0b'

type VideoRef = RefObject<HTMLVideoElement | null>

export function PoseOverlay({ videoRef, pose, show, videoKey, transformKey }: {
  videoRef: VideoRef
  pose: EventPose
  show: boolean
  /** changes when the <video> element is replaced, so the listeners follow it */
  videoKey?: string
  /** changes when the media is zoomed or panned (a CSS transform: no event reaches the video) */
  transformKey?: string
}) {
  const drawRef = useRef<() => void>(() => undefined)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const markRef = useRef<HTMLDivElement>(null)
  const headRef = useRef<HTMLDivElement>(null)
  const { start_s, end_s, state } = pose.hand_at_train
  const hasStretch = state === 'yes' && start_s != null && end_s != null

  useEffect(() => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!show || !video || !canvas) return
    let alive = true
    let vfc = 0

    const draw = (mediaTime?: number) => {
      if (!alive) return
      const ctx = canvas.getContext('2d')
      const cr = canvas.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      const cw = Math.max(1, Math.round(cr.width * dpr))
      const ch = Math.max(1, Math.round(cr.height * dpr))
      if (canvas.width !== cw) canvas.width = cw
      if (canvas.height !== ch) canvas.height = ch
      const t = mediaTime ?? video.currentTime
      const dur = video.duration
      if (headRef.current && Number.isFinite(dur) && dur > 0) headRef.current.style.left = `${Math.min(100, (t / dur) * 100)}%`
      if (markRef.current && hasStretch && Number.isFinite(dur) && dur > 0) {
        markRef.current.style.left = `${(start_s! / dur) * 100}%`
        markRef.current.style.width = `${Math.max(0.5, ((end_s! - start_s!) / dur) * 100)}%`
      }
      if (!ctx) return
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, cr.width, cr.height)
      const frame = frameAt(t, pose.fps, pose.frames, pose.frame_stride)
      if (!frame) return
      const vr = video.getBoundingClientRect()
      const fw = pose.frame_width || video.videoWidth
      const fh = pose.frame_height || video.videoHeight
      const c = videoContentRect(fw, fh, vr.width, vr.height)
      const rect = { x: c.x + vr.left - cr.left, y: c.y + vr.top - cr.top, w: c.w, h: c.h }
      if (rect.w <= 0 || rect.h <= 0) return
      ctx.lineCap = 'round'
      for (const person of frame.p) {
        const k = person.k
        for (const [a, b] of COCO_EDGES) {
          if (!k[a] || !k[b] || k[a][2] < MIN_SCORE || k[b][2] < MIN_SCORE) continue
          const pa = toScreen(k[a], fw, fh, rect)
          const pb = toScreen(k[b], fw, fh, rect)
          ctx.beginPath(); ctx.moveTo(pa.x, pa.y); ctx.lineTo(pb.x, pb.y)
          ctx.strokeStyle = 'rgba(0,0,0,0.55)'; ctx.lineWidth = 4.5; ctx.stroke()
          ctx.strokeStyle = TEAL; ctx.lineWidth = 2.5; ctx.stroke()
        }
        k.forEach((pt, i) => {
          if (pt[2] < MIN_SCORE || WRISTS.includes(i)) return
          const p = toScreen(pt, fw, fh, rect)
          ctx.beginPath(); ctx.arc(p.x, p.y, 3, 0, Math.PI * 2)
          ctx.fillStyle = TEAL; ctx.fill()
        })
      }
      // wrists: amber, with the last second of their positions as a fading trail
      const target = t * pose.fps
      for (const fr of pose.frames) {
        const ageS = (target - fr.f) / pose.fps
        if (ageS < 0 || ageS > TRAIL_S) continue
        const alpha = 0.7 * (1 - ageS / TRAIL_S)
        for (const person of fr.p) for (const w of WRISTS) {
          const pt = person.k[w]
          if (!pt || pt[2] < MIN_SCORE) continue
          const p = toScreen(pt, fw, fh, rect)
          ctx.globalAlpha = alpha
          ctx.beginPath(); ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2)
          ctx.fillStyle = AMBER; ctx.fill()
        }
      }
      ctx.globalAlpha = 1
      for (const person of frame.p) for (const w of WRISTS) {
        const pt = person.k[w]
        if (!pt || pt[2] < MIN_SCORE) continue
        const p = toScreen(pt, fw, fh, rect)
        ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2)
        ctx.fillStyle = AMBER; ctx.fill()
        ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(0,0,0,0.6)'; ctx.stroke()
      }
    }

    const redraw = () => draw()
    drawRef.current = redraw
    const hasVfc = typeof video.requestVideoFrameCallback === 'function'
    const loop = (_now?: number, meta?: { mediaTime?: number }) => {
      if (!alive) return
      draw(meta?.mediaTime)
      vfc = video.requestVideoFrameCallback(loop)
    }
    if (hasVfc) vfc = video.requestVideoFrameCallback(loop)
    const events = hasVfc ? ['seeked', 'loadedmetadata', 'pause'] : ['timeupdate', 'seeked', 'loadedmetadata', 'play', 'pause']
    events.forEach((n) => video.addEventListener(n, redraw))
    window.addEventListener('resize', redraw)
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(redraw) : null
    ro?.observe(video); ro?.observe(canvas)
    draw()

    return () => {
      alive = false
      if (hasVfc && typeof video.cancelVideoFrameCallback === 'function') video.cancelVideoFrameCallback(vfc)
      events.forEach((n) => video.removeEventListener(n, redraw))
      window.removeEventListener('resize', redraw)
      ro?.disconnect()
      drawRef.current = () => undefined
      canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height)
    }
  }, [show, pose, videoRef, videoKey, hasStretch, start_s, end_s])

  // zoom / pan while paused: follow the transform's transition frame by frame, ending on the final position
  useEffect(() => {
    if (!show || transformKey === undefined) return
    let raf = 0
    const until = Date.now() + TRANSFORM_REDRAW_MS
    const tick = () => {
      drawRef.current()
      if (Date.now() < until) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [show, transformKey])

  if (!show) return null
  return (
    <>
      {/* pointer-events-none: the video's own controls stay usable underneath */}
      <canvas ref={canvasRef} aria-hidden className="absolute inset-0 w-full h-full pointer-events-none" />
      {hasStretch && (
        <div
          aria-hidden
          data-testid="pose-strip"
          className="absolute bottom-[3.75rem] left-3 w-40 h-1.5 rounded-full bg-white/25 pointer-events-none"
        >
          <div ref={markRef} className="absolute inset-y-0 rounded-full bg-amber-400" />
          <div ref={headRef} className="absolute -top-0.5 h-2.5 w-0.5 bg-white" />
        </div>
      )}
    </>
  )
}

function PoseLayerInner({ event, videoRef, videoKey, transformKey }: { event: Event; videoRef: VideoRef; videoKey?: string; transformKey?: string }) {
  const { t } = useTranslation('events')
  const switchId = useId()
  // initial value from the profile, then local to this opened event (ModalMedia is keyed on the event)
  const [on, setOn] = useState(() => useAuthStore.getState().user?.pose_overlay_default !== false)
  const { data: pose, isError, isFetching } = useQuery({
    queryKey: queryKeys.eventPose(event.id),
    queryFn: () => eventsApi.getEventPose(event.id),
    enabled: on,
    staleTime: POSE_STALE_MS,
    retry: false,
  })
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()
  return (
    <>
      {pose && <PoseOverlay videoRef={videoRef} pose={pose} show={on} videoKey={videoKey} transformKey={transformKey} />}
      <div
        className="absolute bottom-[4.5rem] right-3 flex items-center gap-2 rounded-full bg-black/60 text-white text-xs px-3 py-1.5 select-none"
        onMouseDown={stop}
        onTouchStart={stop}
        onDoubleClick={stop}
      >
        {/* 404 / 500: say so and lock the switch; while the pose loads the switch is disabled and dimmed */}
        {isError && <span role="status">{t('detail.poseUnavailable')}</span>}
        <label htmlFor={switchId}>{t('detail.showPose')}</label>
        <Switch.Root
          id={switchId}
          checked={on && !isError}
          disabled={isError || (on && !pose)}
          aria-busy={isFetching || undefined}
          onCheckedChange={setOn}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            'data-[state=checked]:bg-teal-500 data-[state=unchecked]:bg-white/30',
          )}
        >
          <Switch.Thumb className="pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
        </Switch.Root>
      </div>
    </>
  )
}

/** The switch and the overlay: only for a superuser, only on an event that has a stored pose (no request otherwise). */
export function PoseLayer({ event, videoRef, videoKey, transformKey }: { event: Event; videoRef: VideoRef; videoKey?: string; transformKey?: string }) {
  // `=== true`: a store that is not loaded, or a test double, never counts as a superuser. The gate (a query) is
  // only mounted for a superuser, so everyone else needs no QueryClient and makes no request.
  const isSuperuser = useAuthStore((s) => s?.user?.is_superuser === true)
  if (!isSuperuser) return null
  return <PoseGate event={event}><PoseLayerInner event={event} videoRef={videoRef} videoKey={videoKey} transformKey={transformKey} /></PoseGate>
}
